/* eslint-disable @typescript-eslint/no-explicit-any */
import styled, { css } from 'styled-components';
import {
  color,
  container,
  fontSize,
  fontWeight,
  screen,
  spacing
} from '../../../theme';

export const StyledWhenInfographic = styled.figure.attrs(
  (props: { ariaHidden?: string }) => ({
    'aria-hidden': props.ariaHidden
  })
)`
  --hour-width: ${spacing('xl3')};
  max-width: ${container('sm')};
  margin: 0;
  margin-top: ${spacing('xl')};

  @media (max-width: ${screen('mdMax')}) {
    display: none;
  }
`;

export const StyledWhenInfographicDuration = styled.div<{
  style?: string;
}>`
  display: grid;
  grid-template-columns: repeat(var(--total-hours), var(--hour-width));
  column-gap: ${spacing('xs2')};
  margin-bottom: ${spacing('xs')};
  align-items: end;
`;

const durationVariants: {
  [key: string]: {
    gridColumn?: string;
    gridTemplateColumns?: string;
    color?: ((props: any) => any) | string;
    backgroundColor?: ((props: any) => any) | string;
    backgroundColorHour?: ((props: any) => any) | string;
  };
} = {
  free: {
    color: color('emerald_500'),
    backgroundColor: color('emerald_150'),
    backgroundColorHour: color('emerald_150')
  },
  flexible: {
    color: color('emerald_500'),
    backgroundColor: color('emerald_150'),
    backgroundColorHour: color('emerald_150'),
    gridColumn: 'span var(--flexible-hours)'
  },
  fixed: {
    gridColumn: 'span var(--fixed-hours)'
  }
};

export const StyledWhenInfographicDurationItem = styled.div<{
  schedule?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${color('navy_500')};
  color: ${color('white')};
  height: ${spacing('lg')};
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('extraBold')};
  line-height: 1;
  vertical-align: text-bottom;
  clip-path: polygon(
    ${spacing('xs')} 0,
    calc(100% - ${spacing('xs')}) 0,
    100% 50%,
    calc(100% - ${spacing('xs')}) 100%,
    ${spacing('xs')} 100%,
    0 50%
  );
  grid-column: span var(--time-span);

  ${({ schedule }) =>
    schedule &&
    durationVariants[schedule] &&
    css`
      color: ${durationVariants[schedule].color};
      background-color: ${durationVariants[schedule].backgroundColor};
    `}

  &.is-free {
    margin-top: 1.2em;
  }
`;

export const StyledWhenInfographicHours = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--total-hours), var(--hour-width));
  column-gap: ${spacing('xs2')};
`;

export const StyledWhenInfographicHoursSection = styled.div<{
  schedule: string;
}>`
  display: grid;
  column-gap: ${spacing('xs2')};
  grid-column: span var(--time-span);
  grid-template-columns: repeat(var(--time-span), 1fr);
  position: relative;

  ${({ schedule }) =>
    schedule &&
    durationVariants[schedule] &&
    css`
      ${StyledWhenInfographicHourItem} {
        background-color: ${durationVariants[schedule].backgroundColorHour};
      }
    `}
`;

export const StyledWhenInfographicHourItem = styled.div`
  background-color: ${color('navy_300')};
  height: ${spacing('xs')};
`;

export const StyledWhenInfographicHourSeparator = styled.div`
  flex-grow: 1;
  margin-left: ${spacing('xs2')};
  margin-right: ${spacing('xs2')};
  height: 1px;
  background-color: ${color('sand_200')};
  position: relative;

  &::after,
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 4px;
    border: solid ${color('sand_200')};
    border-width: 0 1px 1px 0;
    position: absolute;
    top: -2px;
  }

  &::after {
    transform: rotate(-45deg);
    right: 0;
  }

  &::before {
    transform: rotate(135deg);
    left: 0;
  }
`;

export const StyledWhenInfographicHourInfo = styled.div`
  grid-column-start: 1;
  grid-column-end: -1;
  min-height: ${spacing('md')};
  margin-top: ${spacing('xs')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('extraBold')};
  line-height: 1;

  &.is-free {
    justify-content: center;
    position: relative;
    color: ${color('emerald_500')};

    > span {
      position: absolute;
      top: -5.4em;
    }
  }

  &.flex-in {
    > span {
      margin-left: -1.25em;
    }
    ${StyledWhenInfographicHourSeparator} {
      margin-right: 1.25em;
    }
  }

  &.flex-out {
    > span {
      margin-right: -1.25em;
    }
    ${StyledWhenInfographicHourSeparator} {
      margin-left: 1.25em;
    }
  }

  &.fixed {
    .fixed-in {
      margin-left: -1.25em;
    }
    .fixed-out {
      margin-right: -1.25em;
    }
  }

  @media (max-width: ${screen('mdMax')}) {
    display: none;
  }
`;

export const StyledWhenInfographicLabels = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--total-hours), var(--hour-width));
  align-items: center;
  column-gap: ${spacing('xs2')};
  margin-bottom: ${spacing('sm')};
`;

export const StyledWhenInfographicLabelItem = styled.div<{
  schedule: string;
}>`
  text-align: center;
  text-transform: uppercase;
  color: ${color('text_subtle')};
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('black')};
  line-height: 1.15;
  grid-column: span var(--time-span);

  ${({ schedule }) =>
    schedule &&
    durationVariants[schedule] &&
    css`
      grid-column: ${durationVariants[schedule].gridColumn};
      color: ${durationVariants[schedule].color};
    `}

  &.flex-in {
    margin-left: -1em;
    text-align: left;
  }

  &.flex-out {
    margin-right: -1em;
    text-align: right;
  }

  .fixed {
    padding-left: 1em;
    padding-right: 1em;
  }

  ${StyledWhenInfographicHourSeparator} {
    margin-top: ${spacing('xs2')};
  }
`;

export const StyledWhenInfographicLabelItemExtra = styled.div`
  font-weight: ${fontWeight('medium')};
  margin-top: ${spacing('xs2')};
  padding-left: 1em;
  padding-right: 1em;
`;
